import decodePolyline from "polyline";

function sanitizeFilePart(value) {
  return String(value || "activity-route")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "activity-route";
}

function safeDecode(polyline) {
  if (!polyline || typeof polyline !== "string") return [];
  try {
    const decoded = decodePolyline.decode(polyline);
    return decoded.filter(([lat, lon]) => (
      Number.isFinite(lat) && Number.isFinite(lon)
      && lat >= -90 && lat <= 90
      && lon >= -180 && lon <= 180
    ));
  } catch {
    return [];
  }
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const toRad = (value) => (value * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function routeDistanceKm(points) {
  if (!Array.isArray(points) || points.length < 2) return 0;
  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    const [prevLat, prevLon] = points[index - 1];
    const [currLat, currLon] = points[index];
    total += haversineKm(prevLat, prevLon, currLat, currLon);
  }
  return total;
}

function projectToCanvas(points, width, height, padding) {
  const lats = points.map(([lat]) => lat);
  const lons = points.map(([, lon]) => lon);

  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);

  const spanLat = Math.max(maxLat - minLat, 0.000001);
  const spanLon = Math.max(maxLon - minLon, 0.000001);

  const innerWidth = width - (padding * 2);
  const innerHeight = height - (padding * 2);

  const scaleX = innerWidth / spanLon;
  const scaleY = innerHeight / spanLat;
  const scale = Math.min(scaleX, scaleY);

  const routeWidth = spanLon * scale;
  const routeHeight = spanLat * scale;

  const offsetX = (width - routeWidth) / 2;
  const offsetY = (height - routeHeight) / 2;

  return points.map(([lat, lon]) => {
    const x = offsetX + (lon - minLon) * scale;
    const y = height - (offsetY + (lat - minLat) * scale);
    return [x, y];
  });
}

export function createRoutePngDataUrl({
  polyline,
  title = "Activity Route",
  width = 1400,
  height = 860,
}) {
  const points = safeDecode(polyline);
  if (points.length < 2) return null;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const bg = ctx.createLinearGradient(0, 0, 0, height);
  bg.addColorStop(0, "#f8fbff");
  bg.addColorStop(1, "#eef4ff");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, width, height);

  ctx.strokeStyle = "rgba(30, 64, 175, 0.08)";
  ctx.lineWidth = 1;
  for (let x = 0; x < width; x += 80) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
    ctx.stroke();
  }
  for (let y = 0; y < height; y += 80) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();
  }

  const projected = projectToCanvas(points, width, height, 84);

  ctx.lineJoin = "round";
  ctx.lineCap = "round";

  ctx.strokeStyle = "rgba(15, 23, 42, 0.12)";
  ctx.lineWidth = 12;
  ctx.beginPath();
  projected.forEach(([x, y], index) => {
    if (index === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();

  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 8;
  ctx.beginPath();
  projected.forEach(([x, y], index) => {
    if (index === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();

  const routeGradient = ctx.createLinearGradient(0, 0, width, height);
  routeGradient.addColorStop(0, "#2563eb");
  routeGradient.addColorStop(1, "#1d4ed8");
  ctx.strokeStyle = routeGradient;
  ctx.lineWidth = 6;
  ctx.beginPath();
  projected.forEach(([x, y], index) => {
    if (index === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();

  const [startX, startY] = projected[0];
  const [endX, endY] = projected[projected.length - 1];

  const markerDistance = Math.hypot(endX - startX, endY - startY);
  const adjustedEndX = markerDistance < 28 ? endX + 16 : endX;
  const adjustedEndY = markerDistance < 28 ? endY - 16 : endY;

  const drawMarker = (x, y, color) => {
    ctx.beginPath();
    ctx.fillStyle = "#ffffff";
    ctx.arc(x, y, 8, 0, Math.PI * 2);
    ctx.fill();

    ctx.beginPath();
    ctx.fillStyle = color;
    ctx.arc(x, y, 5, 0, Math.PI * 2);
    ctx.fill();
  };

  drawMarker(startX, startY, "#16a34a");
  drawMarker(adjustedEndX, adjustedEndY, "#dc2626");

  ctx.fillStyle = "#0f172a";
  ctx.font = "700 48px sans-serif";
  ctx.fillText(String(title || "Activity Route"), 36, 62);

  const distanceKm = routeDistanceKm(points);
  ctx.fillStyle = "#475569";
  ctx.font = "500 22px sans-serif";
  ctx.fillText(`${points.length} points • ${distanceKm.toFixed(2)} km (approx.)`, 36, 96);

  return canvas.toDataURL("image/png");
}

export function downloadRoutePng({ dataUrl, filename }) {
  if (!dataUrl || !filename) return false;

  const shareApiAvailable = typeof navigator !== "undefined"
    && typeof navigator.share === "function"
    && typeof navigator.canShare === "function";

  const isNativePlatform = (() => {
    try {
      return Boolean(globalThis.Capacitor?.isNativePlatform?.());
    } catch {
      return false;
    }
  })();

  const userAgent = String(globalThis.navigator?.userAgent || "");
  const isAndroidWebView = /\bwv\b/i.test(userAgent);

  if (shareApiAvailable && (isNativePlatform || isAndroidWebView)) {
    try {
      const commaIndex = dataUrl.indexOf(",");
      const base64Payload = commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl;
      const binary = atob(base64Payload);
      const bytes = new Uint8Array(binary.length);
      for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
      }

      const file = new File([bytes], filename, { type: "image/png" });
      if (navigator.canShare({ files: [file] })) {
        void navigator.share({ files: [file], title: filename }).catch(() => {});
        return true;
      }
    } catch {
      // Fall back to browser download when native sharing is unavailable.
    }
  }

  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  return true;
}

export function downloadRoutePngFromActivity({
  polyline,
  activityName,
  activityId,
  activityDate,
}) {
  const dataUrl = createRoutePngDataUrl({
    polyline,
    title: activityName || "Activity Route",
  });
  if (!dataUrl) return false;

  const fileStem = sanitizeFilePart(`${activityDate || "undated"}-${activityName || activityId || "activity-route"}`);
  return downloadRoutePng({
    dataUrl,
    filename: `${fileStem}.png`,
  });
}
